import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Pim, Printing } from '@mummssoftware/utils';
import type {
  EmailInstructions,
  FaxInstructions,
} from '@mummssoftware/utils/api/Printing';
import { getLdapDn, getAgency } from '@mummssoftware/utils/auth';
import { getRoleGroups } from '@mummssoftware/utils/frequencies/frequency-utils';
import {
  AgGridWidgetWrapper,
  ReactDatePicker,
  WidgetHeader,
  usePrintDispatch,
} from '@mummssoftware/common-ui';
import Providers from '../Providers';
import { DEFAULT_LOCALE } from '../i18n';
import defaults from '../translations/en.json';
import {
  PatientComplianceRow,
  ProviderMissed,
  SiteComplianceResult,
  ComplianceRange,
  SitePatient,
  buildVisitCompliancePrintPayload,
  clampComplianceRange,
  complianceFetchRange,
  fetchWindowCovers,
  computeSiteCompliance,
  fetchSiteComplianceData,
  patientListingToSitePatients,
} from './data';
import { PatientComplianceModal } from './PatientComplianceModal';

/**
 * OP-33263: cross patient visit compliance (clinical dashboard).
 *
 * One row per active patient of the site who missed a planned visit in the
 * selected range (at most six weeks): name, MRN and how many visits were
 * missed. Expanding a row lists each administration period that ended in
 * the range short of its plan: role, frequency, period, planned, completed,
 * missed. Scoring is the patient Compliance View's (see
 * computeSiteCompliance), so a listed period is exactly a red box there.
 */

export type CrossPatientComplianceProps = {
  online?: boolean;
  embedded?: boolean;
  agency?: string | null;
  siteId?: string | number | null;
  keycloak: Keycloak.KeycloakInstance;
  smallWidget?: boolean;
  appTitle?: string;
  currentUser?: mumms.User;
  /** Pre-constructed Pim instance with pgRest; created internally if omitted. */
  pim?: Pim;
  theme?: string | null;
  locale?: keyof mumms.translations;
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  onWidgetReady?: (arg: mumms.ComponentName) => void;
};

const ISO_DATE = 'YYYY-MM-DD';
/** The date bar above the table; the sizing wrapper subtracts it. */
const FILTER_BAR_HEIGHT_PX = 42;
const FOOTER_HEIGHT_PX = 26;
/** Typing a date fires several picker changes; fetch and score once they settle. */
const RANGE_DEBOUNCE_MS = 400;
/**
 * The dashboard mounts the expanded widget as a new instance, so without a
 * cache opening the tile repeats the listing, the role groups and both
 * site-wide RPCs (about 8 s on a large site). Rows are kept in memory for a
 * short while so the next mount for the same site reuses them.
 */
const CACHE_TTL_MS = 2 * 60 * 1000;
type SiteData = {
  siteId: string;
  /** Window the rows were fetched for (already includes the visit lookback). */
  fetched: ComplianceRange;
  carePlans: mumms.patientCarePlanProblemFrequency[];
  visits: mumms.pimreaderVisit[];
};
type RefData = {
  user: mumms.User;
  roleGroups: mumms.RoleGroup[];
  patients: SitePatient[];
};
const siteDataCache = new Map<string, SiteData & { at: number }>();
const refDataCache = new Map<string, RefData & { at: number }>();
const fresh = <T extends { at: number }>(entry: T | undefined): T | null =>
  entry && Date.now() - entry.at < CACHE_TTL_MS ? entry : null;
/** Same red as the calendar's missed box. */
const MISSED_COLOR = '#e53935';

/**
 * Content-sized like common-ui AppRoot (embedded): header + body (+ footer
 * when expanded) so the small tile matches the other widgets instead of
 * filling the grid cell and squeezing the header.
 */
const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  min-height: ${FILTER_BAR_HEIGHT_PX}px;
  box-sizing: border-box;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  flex-wrap: wrap;
`;

const BarLabel = styled.span`
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.text.secondary};
  white-space: nowrap;
`;

/** Scrolls the table inside the dashboard's fixed widget height. */
const Scroll = styled.div`
  height: 100%;
  overflow: auto;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const Footer = styled.div`
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  min-height: ${FOOTER_HEIGHT_PX}px;
  box-sizing: border-box;
  color: ${(props) => props.theme.palette.text.secondary};
  background-color: ${(props) => props.theme.palette.background.paper};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`;

const DetailBox = styled.div`
  margin: 0.25rem 0.75rem 0.5rem 2.75rem;
  padding: 0 0.5rem 0.25rem;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const DetailActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 0.25rem;
`;

const MissedCount = styled.span`
  color: ${MISSED_COLOR};
  font-weight: 600;
`;

/* Compact cells like the HCPCS table in Visit Contents. */
const useStyles = makeStyles((theme) => ({
  /* Full widget width; the header band spans it and the columns share the room. */
  table: {
    width: '100%',
  },
  patientCell: {
    width: '46%',
  },
  mrnCell: {
    width: '30%',
  },
  cell: {
    padding: '2px 8px',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  headerCell: {
    padding: '2px 8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    // Sticky header cells take the page background; make the text readable on it.
    backgroundColor: theme.palette.background.default,
    color: theme.palette.getContrastText(theme.palette.background.default),
  },
  toggleCell: {
    width: 40,
    padding: '0 0 0 4px',
  },
  numberCell: {
    textAlign: 'right',
  },
  patientRow: {
    cursor: 'pointer',
  },
  detailCell: {
    padding: 0,
  },
  detailCellClosed: {
    padding: 0,
    borderBottom: 'none',
  },
  emptyCell: {
    padding: '12px 8px',
    textAlign: 'center',
    fontSize: '0.875rem',
  },
}));

const defaultMonthRange = (siteTimezone: string) => {
  const now = moment.tz(siteTimezone);
  return {
    fromDate: now.clone().startOf('month').format(ISO_DATE),
    toDate: now.clone().endOf('month').format(ISO_DATE),
  };
};

const toLocalDate = (isoDate: string): Date =>
  moment(isoDate, ISO_DATE).toDate();

const fromLocalDate = (date: Date): string => moment(date).format(ISO_DATE);

const formatDate = (isoDate: string): string =>
  moment(isoDate, ISO_DATE).format('MMM D, YYYY');

/** "Oct 5 – 11" or "Sep 27 – Oct 3". */
const formatPeriod = (start: string, end: string): string => {
  const s = moment(start, ISO_DATE);
  const e = moment(end, ISO_DATE);
  if (s.isSame(e, 'day')) return s.format('MMM D');
  if (s.isSame(e, 'month')) return `${s.format('MMM D')} – ${e.format('D')}`;
  return `${s.format('MMM D')} – ${e.format('MMM D')}`;
};

const completedText = (period: {
  completed: number;
  inProgress: number;
}): string =>
  period.inProgress
    ? `${period.completed} (+${period.inProgress} ${defaults.inProgress})`
    : String(period.completed);

type LoadState = {
  loading: boolean;
  error: string | null;
};

export const CrossPatientCompliance = ({
  online = true,
  embedded = true,
  agency,
  siteId,
  keycloak,
  smallWidget,
  appTitle,
  currentUser: currentUserProp,
  pim: pimProp,
  theme,
  locale = DEFAULT_LOCALE,
  onClick,
  onRemove,
  doNotShow,
  onWidgetReady,
}: CrossPatientComplianceProps) => (
  <Providers locale={locale} key={locale} theme={theme}>
    <CrossPatientComplianceContent
      online={online}
      embedded={embedded}
      agency={agency}
      siteId={siteId}
      keycloak={keycloak}
      smallWidget={smallWidget}
      appTitle={appTitle}
      currentUser={currentUserProp}
      pim={pimProp}
      onClick={onClick}
      onRemove={onRemove}
      doNotShow={doNotShow}
      onWidgetReady={onWidgetReady}
    />
  </Providers>
);

type PatientRowProps = {
  row: PatientComplianceRow;
  open: boolean;
  onToggle: () => void;
  onOpenCalendar: () => void;
  classes: ReturnType<typeof useStyles>;
};

const numberHeader = (classes: ReturnType<typeof useStyles>, text: string) => (
  <TableCell className={`${classes.headerCell} ${classes.numberCell}`}>
    {text}
  </TableCell>
);

const toggleIcon = (open: boolean) =>
  open ? (
    <KeyboardArrowUpIcon fontSize="small" />
  ) : (
    <KeyboardArrowDownIcon fontSize="small" />
  );

type ProviderRowProps = {
  provider: ProviderMissed;
  open: boolean;
  onToggle: () => void;
  classes: ReturnType<typeof useStyles>;
};

/** One provider (role group) of the patient; expands to its periods. */
const ProviderRow = ({
  provider,
  open,
  onToggle,
  classes,
}: ProviderRowProps) => (
  <>
    <TableRow
      hover
      className={classes.patientRow}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      aria-expanded={open}
      data-testid="cpc-provider"
      data-role-group={provider.roleGroupId}
      data-missed={provider.missed}
    >
      <TableCell className={classes.toggleCell}>
        <IconButton
          size="small"
          aria-label={
            open ? defaults.collapseProvider : defaults.expandProvider
          }
        >
          {toggleIcon(open)}
        </IconButton>
      </TableCell>
      <TableCell className={classes.cell}>{provider.roleGroupName}</TableCell>
      <TableCell className={`${classes.cell} ${classes.numberCell}`}>
        {provider.planned}
      </TableCell>
      <TableCell className={`${classes.cell} ${classes.numberCell}`}>
        {completedText(provider)}
      </TableCell>
      <TableCell className={`${classes.cell} ${classes.numberCell}`}>
        <MissedCount>{provider.missed}</MissedCount>
      </TableCell>
    </TableRow>
    <TableRow data-testid="cpc-provider-detail-row">
      <TableCell
        colSpan={5}
        className={open ? classes.detailCell : classes.detailCellClosed}
      >
        <Collapse in={open} timeout="auto" unmountOnExit>
          <DetailBox>
            <Table size="small" data-testid="cpc-period-table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>
                    {defaults.frequency}
                  </TableCell>
                  <TableCell className={classes.headerCell}>
                    {defaults.period}
                  </TableCell>
                  {numberHeader(classes, defaults.planned)}
                  {numberHeader(classes, defaults.completed)}
                  {numberHeader(classes, defaults.missed)}
                </TableRow>
              </TableHead>
              <TableBody>
                {provider.periods.map((period) => (
                  <TableRow
                    key={`${period.cpfId}-${period.periodStart}`}
                    data-testid="cpc-period"
                    data-period-start={period.periodStart}
                    data-period-end={period.periodEnd}
                    data-missed={period.missed}
                  >
                    <TableCell className={classes.cell}>
                      {period.frequencyLabel}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {formatPeriod(period.periodStart, period.periodEnd)}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.numberCell}`}
                    >
                      {period.planned}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.numberCell}`}
                    >
                      {completedText(period)}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.numberCell}`}
                    >
                      <MissedCount>{period.missed}</MissedCount>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DetailBox>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

/** One patient; expands to a row per provider, each expanding to its periods. */
const PatientRow = ({
  row,
  open,
  onToggle,
  onOpenCalendar,
  classes,
}: PatientRowProps) => {
  const [openProviders, setOpenProviders] = useState<Set<number>>(
    () => new Set(),
  );
  const toggleProvider = (roleGroupId: number) =>
    setOpenProviders((prev) => {
      const next = new Set(prev);
      if (next.has(roleGroupId)) next.delete(roleGroupId);
      else next.add(roleGroupId);
      return next;
    });

  return (
    <>
      <TableRow
        hover
        className={classes.patientRow}
        onClick={onToggle}
        aria-expanded={open}
        data-testid="cpc-row"
        data-patient={row.patientNumber}
      >
        <TableCell className={classes.toggleCell}>
          <IconButton
            size="small"
            aria-label={open ? defaults.collapseRow : defaults.expandRow}
          >
            {toggleIcon(open)}
          </IconButton>
        </TableCell>
        <TableCell className={`${classes.cell} ${classes.patientCell}`}>
          {row.displayName}
        </TableCell>
        <TableCell className={`${classes.cell} ${classes.mrnCell}`}>
          {row.patientNumber}
        </TableCell>
        <TableCell
          className={`${classes.cell} ${classes.numberCell}`}
          data-testid="cpc-missed-count"
        >
          <MissedCount>{row.missedTotal}</MissedCount>
        </TableCell>
      </TableRow>
      <TableRow data-testid="cpc-detail-row" data-patient={row.patientNumber}>
        <TableCell
          colSpan={4}
          className={open ? classes.detailCell : classes.detailCellClosed}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <DetailBox>
              <Table size="small" data-testid="cpc-detail-table">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.toggleCell} />
                    <TableCell className={classes.headerCell}>
                      {defaults.provider}
                    </TableCell>
                    {numberHeader(classes, defaults.planned)}
                    {numberHeader(classes, defaults.completed)}
                    {numberHeader(classes, defaults.missed)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.providers.map((provider) => (
                    <ProviderRow
                      key={provider.roleGroupId}
                      provider={provider}
                      open={openProviders.has(provider.roleGroupId)}
                      onToggle={() => toggleProvider(provider.roleGroupId)}
                      classes={classes}
                    />
                  ))}
                </TableBody>
              </Table>
              <DetailActions>
                <Button
                  size="small"
                  color="primary"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenCalendar();
                  }}
                  data-testid="cpc-open-calendar"
                >
                  {defaults.openPatientCalendar}
                </Button>
              </DetailActions>
            </DetailBox>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CrossPatientComplianceContent = ({
  online,
  agency: agencyProp,
  siteId: siteIdProp,
  keycloak,
  smallWidget,
  appTitle,
  currentUser: currentUserProp,
  pim: pimProp,
  onClick,
  onRemove,
  doNotShow,
  onWidgetReady,
}: Omit<CrossPatientComplianceProps, 'theme' | 'locale'>) => {
  const classes = useStyles();
  const title = appTitle || defaults.crossPatientTitle;

  const agency = useMemo(
    () =>
      agencyProp ||
      currentUserProp?.currentHospiceBin ||
      (keycloak?.tokenParsed
        ? getAgency(getLdapDn(keycloak.tokenParsed))
        : undefined) ||
      undefined,
    [agencyProp, currentUserProp, keycloak],
  );

  const pim = useMemo(
    () =>
      pimProp ??
      new Pim({
        url: process.env.REACT_APP_PIM_BASEURL as string,
        pgRestUrl:
          process.env.REACT_APP_PGREST_PROXY ||
          ((window as any).env?.hosts?.pimReader as string),
        keycloak,
        agency,
      }),
    [pimProp, keycloak, agency],
  );

  // ── Reference data: user (start of week, timezone), role groups, patients ──
  const siteKey = `${agency ?? ''}|${siteIdProp ?? ''}`;
  const cachedRef = fresh(refDataCache.get(siteKey));
  const [currentUser, setCurrentUser] = useState<mumms.User | undefined>(
    currentUserProp ?? cachedRef?.user,
  );
  const [roleGroups, setRoleGroups] = useState<mumms.RoleGroup[] | null>(
    cachedRef?.roleGroups ?? null,
  );
  const [patients, setPatients] = useState<SitePatient[] | null>(
    cachedRef?.patients ?? null,
  );
  const [refState, setRefState] = useState<LoadState>({
    loading: !cachedRef,
    error: null,
  });

  useEffect(() => {
    if (!online || fresh(refDataCache.get(siteKey))) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [user, groups, listing] = await Promise.all([
          currentUserProp
            ? Promise.resolve(currentUserProp)
            : pim.getUserInfo(agency || ''),
          getRoleGroups(keycloak, agency),
          // The dashboard's site, not the user's Hummingbird session site.
          pim.getAllPatients(
            siteIdProp != null && siteIdProp !== '' ? String(siteIdProp) : null,
          ),
        ]);
        if (cancelled) return;
        if (user instanceof Response) {
          throw new Error(user.statusText || 'user info');
        }
        if (listing instanceof Response) {
          throw new Error(listing.statusText || 'patient listing');
        }
        const ref: RefData = {
          user: user as mumms.User,
          roleGroups: groups || [],
          patients: patientListingToSitePatients(listing as mumms.patient[]),
        };
        refDataCache.set(siteKey, { ...ref, at: Date.now() });
        setCurrentUser(ref.user);
        setRoleGroups(ref.roleGroups);
        setPatients(ref.patients);
        setRefState({ loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        setRefState({ loading: false, error: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pim, agency, online, siteIdProp, siteKey]);

  const siteTimezone = currentUser?.siteTimezone || moment.tz.guess();
  const startOfWeek = currentUser?.startOfWeek ?? null;
  const siteId =
    siteIdProp != null && siteIdProp !== '' ? String(siteIdProp) : undefined;

  // ── Date range: current calendar month by default, at most six weeks ──────
  const [range, setRange] = useState<{ fromDate: string; toDate: string }>(() =>
    defaultMonthRange(siteTimezone),
  );
  const rangeInitialised = useRef(false);
  useEffect(() => {
    if (rangeInitialised.current || !currentUser) return;
    rangeInitialised.current = true;
    setRange(defaultMonthRange(siteTimezone));
  }, [currentUser, siteTimezone]);

  const onFromChange = (date: Date | null) => {
    if (!date) return;
    setRange((r) =>
      clampComplianceRange(fromLocalDate(date), r.toDate, 'from'),
    );
  };
  const onToChange = (date: Date | null) => {
    if (!date) return;
    setRange((r) =>
      clampComplianceRange(r.fromDate, fromLocalDate(date), 'to'),
    );
  };

  // ── Plans and visits for the range ────────────────────────────────────────
  // Settled range: pickers update `range` on every change, the data layer
  // follows after a short pause so one typed date does not trigger several
  // fetches and scorings.
  const [debouncedRange, setDebouncedRange] = useState<ComplianceRange>(range);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setDebouncedRange((prev) =>
          prev.fromDate === range.fromDate && prev.toDate === range.toDate
            ? prev
            : range,
        ),
      RANGE_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [range]);

  const [siteData, setSiteData] = useState<SiteData | null>(() =>
    fresh(siteDataCache.get(String(siteIdProp ?? ''))),
  );
  const [dataState, setDataState] = useState<LoadState>({
    loading: !siteData,
    error: null,
  });

  const neededWindow = useMemo(
    () => complianceFetchRange(debouncedRange.fromDate, debouncedRange.toDate),
    [debouncedRange.fromDate, debouncedRange.toDate],
  );
  // Rows already fetched for a window that contains the new one are reused:
  // both RPCs are range filters, so a superset window holds every row a
  // narrower range needs. Moving the dates within the fetched window then
  // costs a rescore only, not two site-wide calls.
  const haveData =
    !!siteData &&
    siteData.siteId === siteId &&
    fetchWindowCovers(siteData.fetched, neededWindow);

  useEffect(() => {
    if (!online || !siteId || !currentUser || haveData) return undefined;
    let cancelled = false;
    setDataState({ loading: true, error: null });
    const fetchRange = neededWindow;
    (async () => {
      try {
        const data = await fetchSiteComplianceData(
          pim,
          siteId,
          fetchRange.fromDate,
          fetchRange.toDate,
        );
        if (cancelled) return;
        const entry: SiteData = { siteId, fetched: fetchRange, ...data };
        siteDataCache.set(siteId, { ...entry, at: Date.now() });
        setSiteData(entry);
        setDataState({ loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        setDataState({ loading: false, error: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, neededWindow, haveData, online, pim, !!currentUser]);

  const result: SiteComplianceResult | null = useMemo(() => {
    if (!siteData || !haveData || !patients || !roleGroups) {
      return null;
    }
    return computeSiteCompliance({
      fromDate: debouncedRange.fromDate,
      toDate: debouncedRange.toDate,
      siteId,
      patients,
      carePlans: siteData.carePlans,
      visits: siteData.visits,
      roleGroups,
      startOfWeek,
      siteTimezone,
    });
  }, [
    siteData,
    haveData,
    siteId,
    patients,
    roleGroups,
    debouncedRange,
    startOfWeek,
    siteTimezone,
  ]);

  // ── Widget ready (once) ───────────────────────────────────────────────────
  const loading = refState.loading || dataState.loading;
  const error = refState.error || dataState.error;
  const widgetReadyCalled = useRef(false);
  useEffect(() => {
    if (!loading && !widgetReadyCalled.current && onWidgetReady) {
      widgetReadyCalled.current = true;
      onWidgetReady('ComplianceCrossPatient' as mumms.ComponentName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Print / fax / email: the listing as shown, through printing-service ──
  const printingService = useMemo(
    () =>
      new Printing({
        url: (window as any).env?.hosts?.printingService as string,
        keycloak,
        agency: agency ?? '',
        currentUser: currentUser as mumms.User,
      }),
    [keycloak, agency, currentUser],
  );
  const [printBusy, setPrintBusy] = useState(false);
  const getPrintPromise = useCallback(
    (
      faxInstructions?: FaxInstructions,
      emailInstructions?: EmailInstructions,
    ) => {
      if (!result) {
        return Promise.reject(new Error('Visit compliance has not loaded'));
      }
      return printingService.printVisitCompliance({
        visitCompliance: buildVisitCompliancePrintPayload(
          result,
          debouncedRange,
          currentUser,
        ),
        faxInstructions,
        emailInstructions,
      });
    },
    [result, debouncedRange, currentUser, printingService],
  );
  const onPrintStart = useCallback(() => setPrintBusy(true), []);
  const onPrintDone = useCallback(() => setPrintBusy(false), []);
  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    onPrintStart,
    onPrintDone,
  );

  // ── Expanded rows and the patient calendar drill-down ─────────────────────
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set());
  const toggleRow = (patientNumber: string) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(patientNumber)) next.delete(patientNumber);
      else next.add(patientNumber);
      return next;
    });
  const [
    selectedPatient,
    setSelectedPatient,
  ] = useState<PatientComplianceRow | null>(null);

  const rows = !error && !loading && result ? result.rows : [];
  let emptyText: string | null = null;
  if (error) emptyText = defaults.complianceError;
  else if (loading || !result) emptyText = defaults.loadingCompliance;
  else if (result.countedThrough === null) emptyText = defaults.noEndedPeriods;
  else if (!result.rows.length) emptyText = defaults.allCompliant;

  let footerText = '';
  if (error) {
    footerText = `${defaults.complianceError} ${error}`;
  } else if (result) {
    if (result.countedThrough === null) {
      footerText = defaults.noEndedPeriods;
    } else {
      footerText = defaults.activePatientsNote
        .replace('{listed}', String(result.rows.length))
        .replace('{total}', String(result.patientsConsidered));
      if (result.countedThrough < debouncedRange.toDate) {
        footerText += ` ${defaults.countedThroughNote.replace(
          '{date}',
          formatDate(result.countedThrough),
        )}`;
      }
    }
  }

  return (
    <Root data-testid="cross-patient-compliance">
      <WidgetHeader
        appTitle={title}
        versionInfo={`Scheduled Visits v${process.env.REACT_APP_SCHEDULED_VISITS_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
        onClick={onClick}
        onRemove={onRemove}
        doNotShow={doNotShow}
        smallWidget={smallWidget}
        onPrint={print}
        onFax={fax}
        onEmail={email}
        showPrint={!smallWidget}
        showFax={!smallWidget}
        disablePrint={printBusy || loading || !!error || !result}
      />
      {!smallWidget && (
        <FilterBar data-testid="cpc-date-range">
          <BarLabel>{defaults.from}</BarLabel>
          <ReactDatePicker
            selectedDate={toLocalDate(range.fromDate)}
            onChange={onFromChange}
            maximumDate={toLocalDate(range.toDate)}
          />
          <BarLabel>{defaults.to}</BarLabel>
          <ReactDatePicker
            selectedDate={toLocalDate(range.toDate)}
            onChange={onToChange}
            minimumDate={toLocalDate(range.fromDate)}
          />
          <BarLabel data-testid="cpc-range-note">
            {defaults.maxRangeNote}
          </BarLabel>
        </FilterBar>
      )}
      {/* Sizing only: the dashboard's full-size / small-widget heights. */}
      <AgGridWidgetWrapper
        id="cross-patient-compliance-body"
        smallWidget={smallWidget}
        heightOffset={
          (smallWidget ? 0 : FILTER_BAR_HEIGHT_PX) + FOOTER_HEIGHT_PX
        }
      >
        <Scroll>
          <Table
            size="small"
            stickyHeader
            className={classes.table}
            data-testid="cpc-table"
          >
            <TableHead>
              <TableRow>
                <TableCell className={classes.toggleCell} />
                <TableCell
                  className={`${classes.headerCell} ${classes.patientCell}`}
                >
                  {defaults.patient}
                </TableCell>
                <TableCell
                  className={`${classes.headerCell} ${classes.mrnCell}`}
                >
                  {defaults.mrn}
                </TableCell>
                <TableCell
                  className={`${classes.headerCell} ${classes.numberCell}`}
                >
                  {defaults.missed}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emptyText ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className={classes.emptyCell}
                    data-testid="cpc-empty"
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <PatientRow
                    key={row.patientNumber}
                    row={row}
                    open={openRows.has(row.patientNumber)}
                    onToggle={() => toggleRow(row.patientNumber)}
                    onOpenCalendar={() => setSelectedPatient(row)}
                    classes={classes}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Scroll>
      </AgGridWidgetWrapper>
      {!smallWidget && <Footer data-testid="cpc-footer">{footerText}</Footer>}
      {selectedPatient && (
        <PatientComplianceModal
          patient={selectedPatient}
          fromDate={debouncedRange.fromDate}
          toDate={debouncedRange.toDate}
          agency={agency}
          keycloak={keycloak}
          currentUser={currentUser}
          pim={pim}
          onClose={() => setSelectedPatient(null)}
        />
      )}
    </Root>
  );
};

export default CrossPatientCompliance;
